import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  findManyNotePages,
  findOneNotePage,
  createNotePage,
  deleteNotePage,
  updateNotePageTitle,
  updateNoteContent,
} from "../db/speakerNoteRepository";
import type { SpeakerNotePage, SpeakerNotePageWithContent } from "../db/schema";

const AUTOSAVE_DELAY_MS = 1000;

export function useReadOnlySpeakerNotes(pdfId: string | null) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [pdfId]);

  const {
    data: pages = [],
    isLoading: loading,
  } = useQuery<SpeakerNotePage[]>({
    queryKey: ["speakerNotePages", pdfId],
    queryFn: () => findManyNotePages(pdfId!),
    enabled: !!pdfId,
  });

  const activePageId = pages[activeIndex]?.id ?? null;

  const {
    data: activePage = null,
    isLoading: contentLoading,
  } = useQuery<SpeakerNotePageWithContent | null>({
    queryKey: ["speakerNotePage", activePageId],
    queryFn: () => findOneNotePage(activePageId!),
    enabled: !!activePageId,
  });

  const selectPage = useCallback(
    (pageId: string) => {
      const index = pages.findIndex((p) => p.id === pageId);
      if (index >= 0) setActiveIndex(index);
    },
    [pages],
  );

  const selectNextPage = useCallback(() => {
    setActiveIndex((prev) => (prev < pages.length - 1 ? prev + 1 : prev));
  }, [pages.length]);

  const selectPrevPage = useCallback(() => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  return {
    pages,
    activePage,
    activeIndex,
    loading,
    contentLoading,
    selectPage,
    selectNextPage,
    selectPrevPage,
  };
}

export function useSpeakerNotePages(pdfId: string | null) {
  const queryClient = useQueryClient();
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: pages = [],
    isLoading: loading,
  } = useQuery<SpeakerNotePage[]>({
    queryKey: ["speakerNotePages", pdfId],
    queryFn: () => findManyNotePages(pdfId!),
    enabled: !!pdfId,
  });

  const {
    data: activePage = null,
    isLoading: contentLoading,
  } = useQuery<SpeakerNotePageWithContent | null>({
    queryKey: ["speakerNotePage", activePageId],
    queryFn: () => findOneNotePage(activePageId!),
    enabled: !!activePageId,
  });

  // 페이지 목록 로드 시 첫 페이지 자동 선택
  useEffect(() => {
    if (pages.length > 0 && !activePageId) {
      setActivePageId(pages[0].id);
    }
  }, [pages, activePageId]);

  // pdfId 변경 시 선택 초기화
  useEffect(() => {
    setActivePageId(null);
  }, [pdfId]);

  const selectPage = useCallback((pageId: string) => {
    setActivePageId(pageId);
  }, []);

  const addPageMutation = useMutation({
    mutationFn: ({ pdfId, order }: { pdfId: string; order: number }) =>
      createNotePage(pdfId, order),
    onSuccess: (newPage) => {
      queryClient.invalidateQueries({ queryKey: ["speakerNotePages", pdfId] });
      setActivePageId(newPage.id);
    },
  });

  const removePageMutation = useMutation({
    mutationFn: (pageId: string) => deleteNotePage(pageId),
    onSuccess: (_data, deletedPageId) => {
      queryClient.invalidateQueries({ queryKey: ["speakerNotePages", pdfId] });
      queryClient.removeQueries({ queryKey: ["speakerNotePage", deletedPageId] });
      if (activePageId === deletedPageId) {
        const remaining = pages.filter((p) => p.id !== deletedPageId);
        setActivePageId(remaining.length > 0 ? remaining[0].id : null);
      }
    },
  });

  const titleMutation = useMutation({
    mutationFn: ({ pageId, title }: { pageId: string; title: string }) =>
      updateNotePageTitle(pageId, title),
    onSuccess: (_data, { pageId, title }) => {
      // 페이지 목록 캐시 optimistic update
      queryClient.setQueryData<SpeakerNotePage[]>(
        ["speakerNotePages", pdfId],
        (old) => old?.map((p) => (p.id === pageId ? { ...p, title } : p)),
      );
      // 활성 페이지 캐시 optimistic update
      queryClient.setQueryData<SpeakerNotePageWithContent | null>(
        ["speakerNotePage", pageId],
        (old) => (old ? { ...old, title } : null),
      );
    },
  });

  const contentMutation = useMutation({
    mutationFn: ({ pageId, content }: { pageId: string; content: string }) =>
      updateNoteContent(pageId, content),
  });

  const handleContentChange = useCallback(
    (content: string) => {
      if (!activePage) return;
      // 로컬 캐시 즉시 업데이트
      queryClient.setQueryData<SpeakerNotePageWithContent | null>(
        ["speakerNotePage", activePage.id],
        (old) => (old ? { ...old, content } : null),
      );

      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(() => {
        contentMutation.mutate({ pageId: activePage.id, content });
      }, AUTOSAVE_DELAY_MS);
    },
    [activePage, queryClient, contentMutation],
  );

  const handleTitleChange = useCallback(
    async (pageId: string, title: string) => {
      await titleMutation.mutateAsync({ pageId, title });
    },
    [titleMutation],
  );

  const addPage = useCallback(async () => {
    if (!pdfId) return;
    await addPageMutation.mutateAsync({ pdfId, order: pages.length });
  }, [pdfId, pages.length, addPageMutation]);

  const removePage = useCallback(
    async (pageId: string) => {
      await removePageMutation.mutateAsync(pageId);
    },
    [removePageMutation],
  );

  return {
    pages,
    activePage,
    loading,
    contentLoading,
    selectPage,
    handleContentChange,
    handleTitleChange,
    addPage,
    removePage,
  };
}
